import { Map } from 'immutable';
import _ from 'lodash';

import BaseStore from './BaseStore';
import FavouritesStore from './FavouritesStore';
import { ActionTypes, FulfilmentType, PurchaseSources } from '../constants/AppConstants';
import config from '../config';

import Order from '../records/Order';
import AppliedVoucher from '../records/order/AppliedVoucher';

export const PENDING_PURCHASE_SOURCE_TTL_MS = 30 * 60 * 1000;

export class OrderStore extends BaseStore {
    deserialize (serialized) {
        let state = new Map({
            isLoading: false,
            discountCodeLoading: false,
            discountCodeMessage: null,
            appliedVoucher: null,
            isLoadingDeliveryOptions: false,
            postalFulfilmentStoredAddress: null,
            handoffDetails: null,
            handoffReferralReported: false,
            purchaseSource: null,
            pendingPurchaseSource: null,
        });
        if (serialized) {
            _.map(serialized, (value, key) => {
                if (key === 'discountCodeMessage' || key === 'postalFulfilmentStoredAddress') {
                    state = state.set(key, null);
                } else if (key === 'appliedVoucher' && value) {
                    state = state.set(key, AppliedVoucher.fromJSON(value));
                } else if ((key === 'purchaseSource' || key === 'pendingPurchaseSource') && value) {
                    state = state.set(key, new Map(value));
                } else if (value) { state = state.set(key, Order.fromJSON(value)); }
            });
        }
        return state;
    }

    setLoading({ isLoading }) {
        return this.setState(this.state.set('isLoading', isLoading));
    }

    setLoadingDeliveryOptions({ isLoadingDeliveryOptions }) {
        return this.setState(this.state.set('isLoadingDeliveryOptions', isLoadingDeliveryOptions));
    }

    getLoading() {
        return this.state.get('isLoading');
    }

    getLoadingDeliveryOptions() {
        return this.state.get('isLoadingDeliveryOptions');
    }

    getDiscountCodeLoading() {
        return this.state.get('discountCodeLoading');
    }

    getDiscountCodeMessage() {
        return this.state.get('discountCodeMessage');
    }

    getAppliedVoucher() {
        return this.state.get('appliedVoucher');
    }

    getCurrent () {
        return this.state.get('current', new Order());
    }

    setCurrent (order) {
        // OrderService.get() resolves null when an order doesn't load. getCurrent()'s default only
        // covers a missing key, so a stored null would reach the header and crash BasketLink (CORE-23745).
        this.setState(this.state.set('current', order ?? new Order()));
    }

    handleOrderPaid ({ order }) {
        let newState = this.state
                        .set('current', new Order()) //Clear the current order
                        .set('paid', order) //Save it as a paid order
                        .set('pendingPurchaseSource', null)
                        .set('appliedVoucher', null); //Clear applied voucher so it doesn't bleed into next cart

        this.setState(newState);
    }

    clearPaidOrder () {
        let newState = this.state.set('paid', new Order());
        this.setState(newState);
    }

    getPaid () {
        return this.state.get('paid');
    }

    setPostalFulfilmentStoredAddress (address) {
        return this.setState(this.state.set('postalFulfilmentStoredAddress', address));
    }

    getPostalFulfilmentStoredAddress () {
        return this.state.get('postalFulfilmentStoredAddress');
    }

    handleOrderCreated ({ order }) {
        this.setCurrent(order);
        this.setState(this.state.set('appliedVoucher', null));
    }

    handleFulfilmentUpdated ({ path, value }) {
        let current = this.getCurrent();
        path.unshift('delivery');

        current = current.setIn(path, value);
        this.setCurrent(current.set('totalPrice', current.getTotalPriceWithDelivery()));
    }

    handleFulfilmentMethodUpdated ({ deliveryOption }) {
        let current = this.getCurrent();

        current = current.set('delivery', current.delivery.setOption(deliveryOption));
        this.setCurrent(current.set('totalPrice', current.getTotalPriceWithDelivery()));
    }

    handlePostalFulfilmentStoredAddress ({ address }) {
        this.setPostalFulfilmentStoredAddress(address);
    }

    handleOrderUpdated ({ order }) {
        let updatedOrder = order
            // Delivery details are deleted
            // when deleting a trip from an order - so we should
            // maintain what we had chosen before
            .set('delivery', this.getCurrent().get('delivery'))
            // API-BUG: Keep the fulfilment values, as this isnt in the api yet
            .set('availableDeliveryOptions', this.getCurrent().get('availableDeliveryOptions'));

        this.setCurrent(updatedOrder);
    }

    handleLeadPassengerUpdated({ trip, leadPassengerName }) {
        const order = this.getCurrent();
        const index = order.trips.indexOf(trip);
        const updatedOrder = order.setIn(['trips', index, 'selfPrintPassenger'], leadPassengerName);
        this.setCurrent(updatedOrder);
    }

    handlePaymentStarted () {
        this.setState(this.state.set('paid', null));
    }

    handleOrderDeleted () {
        let newState = this.state
                        .set('current', new Order())
                        .set('paid', new Order())
                        .set('appliedVoucher', null);
        this.setState(newState);
    }

    handleNewDeliveryOptions({ deliveryOptions }) {
        let updatedOrder = this.getCurrent().setAvailableDeliveryOptions(deliveryOptions);
        let updatedOption;

        // Check if any favourite fares (with delivery attached) exist in the order
        // and set the delivery to match that of the most recently added favourite fare
        if (updatedOrder.includesFavouriteFare(true)) {
            const mostRecentFare = updatedOrder.getFaresIncludedInFavourites(true).last();
            const orderTrip = updatedOrder.trips.filter(trip => trip.hasFare(mostRecentFare)).first();
            const favouriteDelivery = FavouritesStore.getFavourite(mostRecentFare, orderTrip).get('delivery');
            updatedOption = deliveryOptions.find(({ id }) => id === favouriteDelivery.deliveryMethodId);
        } else if (config.get('enableDefaultFulfilmentEticket')) {
            updatedOption = deliveryOptions.find(({ fulfilmentMethod }) => fulfilmentMethod === FulfilmentType.E_TICKET);
        } else if (config.get('enableDefaultFulfilmentSmartcard')) {
            updatedOption = deliveryOptions.find(({ fulfilmentMethod }) => fulfilmentMethod === FulfilmentType.SMARTCARD);
        }

        if (updatedOption) {
            updatedOrder = updatedOrder.setDeliveryOption(updatedOption);
        }

        this.setCurrent(updatedOrder);
    }

    handleTaxiBookingAvailability ({ onwardTaxiBookingAvailable }) {
        this.setState(this.state.set('onwardTaxiBookingAvailable', onwardTaxiBookingAvailable));
    }

    isTaxiBookingAvailable () {
        // QUICKFIX: The store defaults this key to an order for some reason
        if (_.isBoolean(this.state.get('onwardTaxiBookingAvailable'))) {
            return this.state.get('onwardTaxiBookingAvailable', false);
        }
        return false;
    }

    handleCheckDiscountCodeStarted () {
        this.setState(this.state.set('discountCodeLoading', true));
    }

    hideDiscountCodeMessage () {
        const newState = this.state.set('discountCodeMessage', '');
        this.setState(newState);
    }

    handleCheckDiscountCodeFinished ({ updatedOrder }) {
        this.handleOrderUpdated({ order: updatedOrder });

        const newState = this.state
            .set('discountCodeLoading', false)
            .set('discountCodeMessage', _.get(updatedOrder, 'discount.description'));
        this.setState(newState);

        // hiding the message after 2 sec
        setTimeout(() => {
            this.hideDiscountCodeMessage();
        }, 2000);

    }

    handleCheckDiscountCodeFailed ({ error }) {
        const newState = this.state
            .set('discountCodeLoading', false)
            .set('discountCodeMessage', _.get(error, 'body.errors') && _.get(error, 'body.errors')[0].message);
        this.setState(newState);

        // hiding the message after 2 sec
        setTimeout(() => {
            this.hideDiscountCodeMessage();
        }, 2000);
    }

    handleApplyVoucherCodeFinished ({ order, voucher }) {
        this.handleOrderUpdated({ order });
        this.setState(this.state.set('appliedVoucher', voucher));
    }

    handleRemoveVoucherCodeFinished ({ order }) {
        this.handleOrderUpdated({ order });
        this.setState(this.state.set('appliedVoucher', null));
    }

    handleBasketModified (payload) {
        this.handleOrderUpdated(payload);
        if (this.state.get('appliedVoucher')) {
            this.setState(this.state.set('appliedVoucher', null));
        }
    }

    handleBasketEmptied () {
        this.handleOrderDeleted();
        if (this.state.get('appliedVoucher')) {
            this.setState(this.state.set('appliedVoucher', null));
        }
    }


    handleContactDetailsUpdated({email, telephoneNumber}) {
        const updated = this.getCurrent().merge({email, telephoneNumber});
        this.setCurrent(updated);
    }

    handleOrderHandoffUpdated({ handoffDetails }) {
        return this.setState(this.state.set('handoffDetails', handoffDetails));
    }

    getHandoffDetails() {
        return this.state.get('handoffDetails');
    }

    handleHandoffReferralReported({ referralReported }) {
        return this.setState(this.state.set('handoffReferralReported', referralReported));
    }

    getHandoffReferralReported() {
        return this.state.get('handoffReferralReported');
    }

    // Only the landing page's param is read; later page changes just extend a pending source
    handleRouteChanged({ location }) {
        const isLanding = !this.landingHandled;
        this.landingHandled = true;

        const landedSource = isLanding ? location?.query?.purchaseSource : null;
        const source = Object.values(PurchaseSources).includes(landedSource)
            ? landedSource
            : this.getPendingPurchaseSource();
        if (!source) { return; }

        const pending = new Map({ source, expiresAt: Date.now() + PENDING_PURCHASE_SOURCE_TTL_MS });
        this.setState(this.state.set('pendingPurchaseSource', pending));
    }

    getPendingPurchaseSource() {
        const pending = this.state.get('pendingPurchaseSource');
        return pending && pending.get('expiresAt') > Date.now() ? pending.get('source') : null;
    }

    handlePurchaseSourceUpdated({ orderId, purchaseSource }) {
        const marker = new Map({ orderId, source: purchaseSource });
        return this.setState(this.state.set('purchaseSource', marker));
    }

    getPurchaseSource(orderId) {
        const marker = this.state.get('purchaseSource');
        return marker && marker.get('orderId') === orderId ? marker.get('source') : null;
    }

    getActionHandlers () {
        return {
            [ ActionTypes.ORDER_LOADING ]: this.setLoading,
            [ ActionTypes.ORDER_DELIVERY_OPTIONS_LOADING ]: this.setLoadingDeliveryOptions,
            [ ActionTypes.ORDER_CREATED ]: this.handleOrderCreated,
            [ ActionTypes.ORDER_REFRESH_LOADED ]: this.handleOrderUpdated,
            [ ActionTypes.ORDER_TRIP_CREATED ]: this.handleBasketModified,
            [ ActionTypes.ORDER_TRIP_UPDATED ]: this.handleBasketModified,
            [ ActionTypes.ORDER_TRIP_DELETED ]: this.handleBasketModified,
            [ ActionTypes.SET_ORDER_CHARITY_OPTIONS_SUCCESS ]: this.handleOrderUpdated,
            [ ActionTypes.DELETE_ORDER_CHARITY_OPTIONS_SUCCESS ]: this.handleOrderUpdated,
            [ ActionTypes.ORDER_TRIP_LEAD_PASSENGER_UPDATED ]: this.handleLeadPassengerUpdated,
            [ ActionTypes.ORDER_PLUSBUS_UPDATED ]: this.handleOrderUpdated,
            [ ActionTypes.ORDER_PAID ]: this.handleOrderPaid,
            [ ActionTypes.ORDER_DELIVERY_OPTIONS_LOADED ]: this.handleNewDeliveryOptions,
            [ ActionTypes.ORDER_UPDATED ]: this.handleOrderUpdated,
            [ ActionTypes.FULFILMENT_UPDATED ]: this.handleFulfilmentUpdated,
            [ ActionTypes.FULFILMENT_METHOD_UPDATED ]: this.handleFulfilmentMethodUpdated,
            [ ActionTypes.POSTAL_FULFILMENT_STORED_ADDRESS_UPDATED ]: this.handlePostalFulfilmentStoredAddress,
            [ ActionTypes.ADDRESS_CREATED ]: this.handlePostalFulfilmentStoredAddress,
            [ ActionTypes.PAYMENT_AUTH_STARTED ]: this.handlePaymentStarted,
            [ ActionTypes.PAY360_SESSION_CREATED ]: this.handlePaymentStarted,
            [ ActionTypes.BASKET_CLEARED ]: this.handleBasketEmptied,
            [ ActionTypes.ORDER_EXPIRED ]: this.handleBasketEmptied,
            [ ActionTypes.LOGGED_OUT ]: this.handleBasketEmptied,
            [ ActionTypes.LOGGED_IN ]: this.clearPaidOrder,

            [ ActionTypes.ORDER_TAXI_BOOKING_AVAILABILITY ]: this.handleTaxiBookingAvailability,

            [ ActionTypes.ORDER_LOAD_STARTED ]: this.handleOrderDeleted,
            [ ActionTypes.ORDER_LOADED ]: this.handleOrderCreated,
            [ ActionTypes.ORDER_LOAD_FAILED ]: this.handleOrderDeleted,
            [ ActionTypes.ORDER_CONTACT_DETAILS_UPDATED ]: this.handleContactDetailsUpdated,
            [ ActionTypes.ORDER_HANDOFF_UPDATED ]: this.handleOrderHandoffUpdated,
            [ ActionTypes.ORDER_HANDOFF_REFERRAL_REPORTED ]: this.handleHandoffReferralReported,
            [ ActionTypes.ORDER_PURCHASE_SOURCE_UPDATED ]: this.handlePurchaseSourceUpdated,
            [ ActionTypes.ROUTE_CHANGED ]: this.handleRouteChanged,

            [ ActionTypes.APPLY_DISCOUNT_CODE_STARTED ]: this.handleCheckDiscountCodeStarted,
            [ ActionTypes.APPLY_DISCOUNT_CODE_FINISHED ]: this.handleCheckDiscountCodeFinished,
            [ ActionTypes.APPLY_DISCOUNT_CODE_FAILED ]: this.handleCheckDiscountCodeFailed,

            [ ActionTypes.DELETE_DISCOUNT_CODE_STARTED ]: this.handleCheckDiscountCodeStarted,
            [ ActionTypes.DELETE_DISCOUNT_CODE_FINISHED ]: this.handleCheckDiscountCodeFinished,
            [ ActionTypes.DELETE_DISCOUNT_CODE_FAILED ]: this.handleCheckDiscountCodeFailed,

            [ ActionTypes.APPLY_VOUCHER_CODE_FINISHED ]: this.handleApplyVoucherCodeFinished,
            [ ActionTypes.REMOVE_VOUCHER_CODE_FINISHED ]: this.handleRemoveVoucherCodeFinished,

        };
    }
}

const storeInstance = new OrderStore({ storageKey: 'orders' });


export default storeInstance;
